<template>
  <div class="hsb-wrap">
    <div class="hsb-bar">

      <!-- 키워드 -->
      <div class="hsb-field hsb-keyword" style="position:relative;">
        <label class="hsb-label">호텔명</label>
        <input
          v-model="keyword"
          @input="onKeywordInput"
          @focus="onKeywordInput"
          type="text"
          class="hsb-input"
          placeholder="호텔명(국문/영문) 검색 (선택)"
          autocomplete="off"
        />

        <!-- 자동완성 드롭다운 -->
        <div v-if="keywordOpen && keywordSuggestions.length" class="hsb-suggest-panel" @click.stop>
          <div
            v-for="h in keywordSuggestions"
            :key="h.hotel_id"
            class="suggest-item"
            @click="selectKeyword(h)"
          >
            <span class="suggest-name">{{ h.hotel_name }}</span>
            <span class="suggest-loc">{{ h.state_province || h.city || '' }}</span>
          </div>
        </div>
      </div>

      <div class="hsb-divider"></div>

      <!-- 지역 -->
      <div class="hsb-field" :class="{ 'hsb-error': err.city }">
        <label class="hsb-label">지역 <em>*</em></label>
        <select v-model="city" class="hsb-input">
          <option value="">지역 선택</option>
          <option
            v-for="c in cityList"
            :key="c.state_province"
            :value="c.state_province"
          >{{ c.pref_name_ko }}</option>
        </select>
      </div>

      <!-- 숙소유형리스트 -->
      <div class="hsb-field">
        <label class="hsb-label">숙소유형</label>
        <select v-model="typeCode" class="hsb-input">
          <option value="">전체 유형</option>
          <option v-for="t in typeList" :key="t.code" :value="t.code">
            {{ t.label_ko }}
          </option>
        </select>
      </div>

      <div class="hsb-divider"></div>

      <!-- 체크인 - 체크아웃 (통합 캘린더) -->
      <div
        class="hsb-field hsb-dates"
        :class="{ 'hsb-error': err.checkIn || err.checkOut }"
        @click="toggleCalendar"
      >
        <label class="hsb-label">체크인 - 체크아웃 <em>*</em></label>
        <div class="hsb-date-summary">
          <span v-if="checkIn">{{ formatDisplay(checkIn) }}</span>
          <span v-else class="hsb-placeholder">날짜 선택</span>
          <span class="hsb-arrow">→</span>
          <span v-if="checkOut">{{ formatDisplay(checkOut) }}</span>
          <span v-else class="hsb-placeholder">-</span>
        </div>

        <!-- 캘린더 패널 -->
        <div v-if="calendarOpen" class="hsb-cal-panel" @click.stop>
          <div class="cal-header">
            <button type="button" class="cal-nav" @click="navCalendar(-1)">‹</button>
            <div class="cal-months-label">
              <span>{{ calendarMonths[0].label }}</span>
              <span>{{ calendarMonths[1].label }}</span>
            </div>
            <button type="button" class="cal-nav" @click="navCalendar(1)">›</button>
          </div>

          <div class="cal-months">
            <div class="cal-month" v-for="(m, mi) in calendarMonths" :key="mi">
              <div class="cal-weekdays">
                <span v-for="wd in weekdayLabels" :key="wd">{{ wd }}</span>
              </div>
              <div class="cal-days">
                <span
                  v-for="(cell, ci) in m.days"
                  :key="ci"
                  class="cal-day"
                  :class="{
                    'cal-empty': !cell.date,
                    'cal-disabled': cell.disabled,
                    'cal-start': cell.isStart,
                    'cal-end': cell.isEnd,
                    'cal-in-range': cell.inRange,
                    'cal-today': cell.isToday
                  }"
                  @click="cell.date && selectDate(cell.date, cell.disabled)"
                >{{ cell.day }}</span>
              </div>
            </div>
          </div>

          <div class="cal-footer">
            <span class="cal-hint">{{ calendarHint }}</span>
            <button type="button" class="cal-clear" @click.stop="clearDates">초기화</button>
          </div>
        </div>
      </div>

      <!-- 박수 -->
      <div v-if="nightCount > 0" class="hsb-nights">{{ nightCount }}박</div>

      <div class="hsb-divider"></div>

      <!-- 인원/객실 -->
      <div class="hsb-field hsb-guests" @click="guestOpen = !guestOpen" style="cursor:pointer; position:relative;">
        <label class="hsb-label">인원/객실 <em>*</em></label>
        <div class="hsb-guest-summary">
          성인 {{ adultCount }}명 · {{ roomCount }}실
          <span v-if="children.length"> · 어린이 {{ children.length }}명</span>
        </div>

        <!-- 인원 드롭다운 -->
        <div v-if="guestOpen" class="hsb-guest-panel" @click.stop>
          <div class="gp-row">
            <span>객실 수</span>
            <div class="gp-ctrl">
              <button @click="roomCount = Math.max(1, roomCount - 1)" class="gp-btn">－</button>
              <span class="gp-val">{{ roomCount }}</span>
              <button @click="roomCount = Math.min(9, roomCount + 1)" class="gp-btn">＋</button>
            </div>
          </div>
          <div class="gp-row">
            <span>성인</span>
            <div class="gp-ctrl">
              <button @click="adultCount = Math.max(roomCount, adultCount - 1)" class="gp-btn">－</button>
              <span class="gp-val">{{ adultCount }}</span>
              <button @click="adultCount = Math.min(36, adultCount + 1)" class="gp-btn">＋</button>
            </div>
          </div>
          <div class="gp-row">
            <span>어린이</span>
            <div class="gp-ctrl">
              <button @click="removeChild" :disabled="!children.length" class="gp-btn">－</button>
              <span class="gp-val">{{ children.length }}</span>
              <button @click="addChild" :disabled="children.length >= 9" class="gp-btn">＋</button>
            </div>
          </div>
          <div v-if="children.length" class="gp-ages">
            <div v-for="(age, i) in children" :key="i" class="gp-age-row">
              <span>어린이 {{ i+1 }} 나이</span>
              <select v-model.number="children[i]" class="gp-age-sel">
                <option v-for="a in 18" :key="a-1" :value="a-1">{{ a-1 }}세</option>
              </select>
            </div>
          </div>
          <button @click="guestOpen = false" class="gp-done">완료</button>
        </div>
      </div>

      <!-- 검색 버튼 -->
      <button @click="doSearch" class="hsb-btn">
        🔍 검색
      </button>

    </div>

    <!-- 에러 메시지 -->
    <div v-if="errMsg" class="hsb-errmsg">⚠️ {{ errMsg }}</div>

  </div>
</template>


<script>
export default {
  name: 'HotelSearchBar',

  data() {
    const t = new Date();
    return {
      cityList:   [],
      typeList:   [],         // 숙소유형리스트
      keyword:    '',
      keywordOpen: false,
      keywordSuggestions: [], // ← computed에서 data로 변경 (서버 검색 결과 저장용)
      searchTimer: null,      // ← 디바운스 타이머
      searchToken: 0,         // ← 응답 순서 꼬임 방지용
      city:       '',
      typeCode:   '',
      checkIn:    '',
      checkOut:   '',
      roomCount:  1,
      adultCount: 2,
      children:   [],
      guestOpen:  false,
      calendarOpen: false,
      calendarBase: new Date(t.getFullYear(), t.getMonth(), 1),
      weekdayLabels: ['일', '월', '화', '수', '목', '금', '토'],
      errMsg:     '',
      err: { city: false, checkIn: false, checkOut: false }
    };
  },

  computed: {
    today() {
      return new Date().toISOString().split('T')[0];
    },
    maxDate() {
      const d = new Date();
      d.setFullYear(d.getFullYear() + 1);
      return d.toISOString().split('T')[0];
    },
    nightCount() {
      if (!this.checkIn || !this.checkOut) return 0;
      return Math.round(
        (new Date(this.checkOut) - new Date(this.checkIn)) / 86400000
      );
    },
    calendarMonths() {
      const base = this.calendarBase;
      const m0 = this.buildMonth(base.getFullYear(), base.getMonth());
      const next = new Date(base.getFullYear(), base.getMonth() + 1, 1);
      const m1 = this.buildMonth(next.getFullYear(), next.getMonth());
      return [m0, m1];
    },
    calendarHint() {
      if (!this.checkIn) return '체크인 날짜를 선택해주세요';
      if (!this.checkOut) return '체크아웃 날짜를 선택해주세요';
      return `${this.nightCount}박 일정이 선택되었습니다`;
    }
    // ← keywordSuggestions computed는 삭제 (data 프로퍼티로 대체됨)
  },

  created() {
    this.loadCities();
    this.loadAccomType();
    // this.loadHotelList();  ← 삭제: 더 이상 전체 호텔을 미리 안 받아옴
    document.addEventListener('click', this.closePanels);
  },

  beforeUnmount() {
    document.removeEventListener('click', this.closePanels);
    clearTimeout(this.searchTimer);
  },

  methods: {
    async loadCities() {
      try
      {
        const result = await this.$api('/api/perfectrureList', { param: ['JP'] });
        if (result?.length) this.cityList = result;
      }
      catch(e)
      {
        console.error('[SearchBar] 지역정보 로딩실패:', e);
      }
    },

    async loadAccomType() {
      try
      {
        const result = await this.$api('/api/rakutenHotelType', {});
        if (result?.length) this.typeList = result;
      }
      catch(e)
      {
        console.error('[SearchBar] 숙소유형 로딩실패:', e);
      }
    },

    onKeywordInput() {
      const kw = this.keyword.trim();
      this.keywordOpen = kw.length > 0;

      clearTimeout(this.searchTimer);

      if (!kw) {
        this.keywordSuggestions = [];
        return;
      }

      this.searchTimer = setTimeout(() => this.fetchSuggestions(kw), 300);
    },

    async fetchSuggestions(kw) {
      const myToken = ++this.searchToken;
      try
      {
        const like = `%${kw}%`;
        const result = await this.$api('/api/searchRakutenHotels', {
          param: [like, like, like, like]
        });

        // 늦게 도착한 응답이 최신 검색결과를 덮어쓰는 것 방지
        if (myToken !== this.searchToken) return;

        this.keywordSuggestions = (result || []).map(h => ({
          ...h,
          hotel_id: String(h.hotel_id)
        }));
      }
      catch(e)
      {
        console.error('[SearchBar] 자동완성 검색 실패:', e);
      }
    },

    selectKeyword(hotel) {
      this.keyword = hotel.hotel_name;
      if (hotel.state_province) this.city = hotel.state_province;
      this.keywordOpen = false;
    },

    pad(n) {
      return String(n).padStart(2, '0');
    },

    toDateStr(year, month, day) {
      return `${year}-${this.pad(month + 1)}-${this.pad(day)}`;
    },

    addDays(dateStr, n) {
      const d = new Date(dateStr);
      d.setDate(d.getDate() + n);
      return d.toISOString().split('T')[0];
    },

    isDateDisabled(dateStr) {
      if (dateStr < this.today) return true;
      if (dateStr > this.maxDate) return true;
      // 체크아웃 대기 중엔 체크인 + 30일까지만 허용
      if (this.checkIn && !this.checkOut) {
        const cap = this.addDays(this.checkIn, 30);
        if (dateStr > cap) return true;
      }
      return false;
    },

    buildMonth(year, month) {
      const firstDay = new Date(year, month, 1);
      const startWeekday = firstDay.getDay();
      const lastDate = new Date(year, month + 1, 0).getDate();
      const days = [];

      for (let i = 0; i < startWeekday; i++) {
        days.push({ date: null, day: '' });
      }

      for (let d = 1; d <= lastDate; d++) {
        const dateStr = this.toDateStr(year, month, d);
        days.push({
          date: dateStr,
          day: d,
          disabled: this.isDateDisabled(dateStr),
          isStart: dateStr === this.checkIn,
          isEnd: dateStr === this.checkOut,
          inRange: !!(this.checkIn && this.checkOut && dateStr > this.checkIn && dateStr < this.checkOut),
          isToday: dateStr === this.today
        });
      }

      while (days.length % 7 !== 0) {
        days.push({ date: null, day: '' });
      }

      return { label: `${year}년 ${month + 1}월`, days };
    },

    toggleCalendar() {
      this.calendarOpen = !this.calendarOpen;
      if (this.calendarOpen) this.guestOpen = false;
    },

    navCalendar(dir) {
      const b = this.calendarBase;
      this.calendarBase = new Date(b.getFullYear(), b.getMonth() + dir, 1);
    },

    selectDate(dateStr, disabled) {
      if (disabled) return;

      if (!this.checkIn || (this.checkIn && this.checkOut)) {
        // 새로운 체크인 선택 — 캘린더를 체크인 달로 재정렬
        this.checkIn  = dateStr;
        this.checkOut = '';

        const [y, m] = dateStr.split('-').map(Number);
        this.calendarBase = new Date(y, m - 1, 1);   // ✅ 추가

      } else {
        // 체크인은 있고 체크아웃 대기 중
        if (dateStr <= this.checkIn) {
          this.checkIn  = dateStr;
          this.checkOut = '';

          const [y, m] = dateStr.split('-').map(Number);
          this.calendarBase = new Date(y, m - 1, 1);   // ✅ 추가

        } else {
          this.checkOut = dateStr;
          this.calendarOpen = false;
        }
      }
    },

    clearDates() {
      this.checkIn = '';
      this.checkOut = '';
    },

    formatDisplay(dateStr) {
      if (!dateStr) return '';
      const [y, m, d] = dateStr.split('-');
      return `${y}.${m}.${d}`;
    },

    addChild()    { if (this.children.length < 9) this.children.push(5); },
    removeChild() { if (this.children.length) this.children.pop(); },

    closePanels(e) {
      if (!e.target.closest('.hsb-guests'))  this.guestOpen    = false;
      if (!e.target.closest('.hsb-dates'))   this.calendarOpen = false;
      if (!e.target.closest('.hsb-keyword')) this.keywordOpen  = false;
    },

    validate() {
      this.err = { city: false, checkIn: false, checkOut: false };
      if (!this.city)
      {
        this.err.city = true;
        return '지역을 선택해주세요.';
      }
      if (!this.checkIn)
      {
        this.err.checkIn = true;
        return '체크인 날짜를 선택해주세요.';
      }
      if (!this.checkOut)
      {
        this.err.checkOut = true;
        return '체크아웃 날짜를 선택해주세요.';
      }
      if (this.checkOut <= this.checkIn)
      {
        this.err.checkOut = true;
        return '체크아웃은 체크인 이후여야 합니다.';
      }
      return '';
    },

    doSearch() {
      this.errMsg = this.validate();
      if (this.errMsg) return;

      this.$router.push({
        path:  '/rtxHotelSearch',
        query: {
          city:       this.city,
          typeCode:   this.typeCode,
          keyword:    this.keyword    || '',
          checkIn:    this.checkIn,
          checkOut:   this.checkOut,
          roomCount:  this.roomCount,
          adultCount: this.adultCount,
          children:   this.children.join(',')
        }
      });
    }
  }
};
</script>


<style scoped>
.hsb-wrap {
  font-family: 'Noto Sans KR', sans-serif;
  padding: 4px 0;
}

.hsb-title {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}
.hsb-icon { font-size: 28px; }
.hsb-title h3 {
  font-size: 1.3rem;
  font-weight: 800;
  color: #1a1a2e;
  margin: 0;
}
.hsb-title p {
  font-size: 0.85rem;
  color: #888;
  margin: 0;
}

/* 검색바 */
.hsb-bar {
  display: flex;
  align-items: stretch;
  background: #fff;
  border: 2px solid #1a1a2e;
  border-radius: 14px;
  overflow: visible;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  flex-wrap: wrap;
}

.hsb-field {
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 12px 16px;
  flex: 1;
  min-width: 0;
  transition: background 0.15s;
}
.hsb-field:hover     { background: #f8f9ff; }
.hsb-field.hsb-error { background: #fff5f5; }
.hsb-guests          { cursor: pointer; position: relative; }
.hsb-dates            { cursor: pointer; position: relative; }

.hsb-label {
  font-size: 10px;
  font-weight: 700;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
  white-space: nowrap;
}
.hsb-label em { color: #e53e3e; font-style: normal; }

.hsb-input {
  border: none;
  outline: none;
  background: transparent;
  font-size: 14px;
  font-weight: 600;
  color: #1a1a2e;
  width: 100%;
  padding: 0;
  cursor: pointer;
}
.hsb-input::placeholder { color: #bbb; font-weight: 400; }

.hsb-guest-summary {
  font-size: 14px;
  font-weight: 600;
  color: #1a1a2e;
  white-space: nowrap;
}

.hsb-date-summary {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 600;
  color: #1a1a2e;
  white-space: nowrap;
}
.hsb-placeholder { color: #bbb; font-weight: 400; }
.hsb-arrow { color: #bbb; font-size: 12px; }

/* 키워드 자동완성 */
.hsb-suggest-panel {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  background: #fff !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 12px !important;
  padding: 6px;
  box-shadow: 0 8px 30px rgba(0,0,0,0.12) !important;
  z-index: 200;
  max-height: 320px;
  overflow-y: auto;
}
.suggest-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  transition: background 0.12s;
  border-radius: 8px !important;
}
.suggest-item:hover { background: #f0f4ff; }
.suggest-name {
  font-weight: 600;
  color: #1a1a2e;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.suggest-loc {
  font-size: 11px;
  color: #999;
  white-space: nowrap;
  flex-shrink: 0;
}

.hsb-nights {
  background: #ebf4ff;
  color: #2b6cb0;
  font-size: 13px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 20px;
  white-space: nowrap;
  align-self: center;
  flex-shrink: 0;
}

.hsb-divider {
  width: 1px;
  background: #e2e8f0;
  margin: 10px 0;
  flex-shrink: 0;
}

/* 검색 버튼 */
.hsb-btn {
  padding: 0 28px;
  background: #1a1a2e;
  color: #fff;
  border: none;
  border-radius: 0 12px 12px 0;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.2s;
  flex-shrink: 0;
}
.hsb-btn:hover { background: #86cd53; }

/* 에러 */
.hsb-errmsg {
  margin-top: 8px;
  font-size: 12px;
  color: #e53e3e;
  padding-left: 4px;
}

/* 인원 드롭다운 */
.hsb-guest-panel {
  position: absolute;
  top: calc(100% + 10px);
  left: 0;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 8px 30px rgba(0,0,0,0.12);
  z-index: 200;
  min-width: 240px;
}

.gp-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #f0f0f0;
  font-size: 14px;
  font-weight: 600;
}
.gp-row:last-of-type { border-bottom: none; }

.gp-ctrl {
  display: flex;
  align-items: center;
  gap: 12px;
}
.gp-btn {
  width: 28px;
  height: 28px;
  border: 1px solid #ddd;
  border-radius: 50%;
  background: #fff;
  cursor: pointer;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
}
.gp-btn:hover:not(:disabled) { border-color: #1a1a2e; background: #f0f0f0; }
.gp-btn:disabled { opacity: 0.3; cursor: not-allowed; }
.gp-val {
  min-width: 20px;
  text-align: center;
  font-size: 15px;
  font-weight: 700;
}

.gp-ages      { padding: 8px 0; }
.gp-age-row   {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  font-size: 13px;
  color: #555;
}
.gp-age-sel {
  width: 80px;
  padding: 4px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 13px;
}
.gp-done {
  width: 100%;
  margin-top: 12px;
  padding: 8px;
  background: #1a1a2e;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}

/* 날짜 범위 캘린더 */
.hsb-cal-panel {
  position: absolute;
  top: calc(100% + 10px);
  left: 0;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 8px 30px rgba(0,0,0,0.12);
  z-index: 200;
  min-width: 560px;
}

.cal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
.cal-nav {
  width: 28px;
  height: 28px;
  border: 1px solid #ddd;
  border-radius: 50%;
  background: #fff;
  cursor: pointer;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
  flex-shrink: 0;
}
.cal-nav:hover { border-color: #1a1a2e; background: #f0f0f0; }

.cal-months-label {
  display: flex;
  gap: 80px;
  font-size: 14px;
  font-weight: 700;
  color: #1a1a2e;
}

.cal-months {
  display: flex;
  gap: 24px;
}
.cal-month { flex: 1; min-width: 0; }

.cal-weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  font-size: 11px;
  color: #999;
  text-align: center;
  margin-bottom: 6px;
  font-weight: 700;
}

.cal-days {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
}
.cal-day {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 32px;
  font-size: 13px;
  border-radius: 8px;
  cursor: pointer;
  color: #1a1a2e;
}
.cal-day:hover:not(.cal-disabled):not(.cal-empty) { background: #f0fff4; }
.cal-empty { cursor: default; }
.cal-disabled { color: #ddd; cursor: not-allowed; }
.cal-today { font-weight: 800; text-decoration: underline; text-underline-offset: 3px; }
.cal-in-range { background: #eafbe9; border-radius: 0; }
.cal-start, .cal-end {
  background: #1a1a2e;
  color: #fff;
  font-weight: 700;
}
.cal-start { border-radius: 8px 0 0 8px; }
.cal-end   { border-radius: 0 8px 8px 0; }
.cal-start.cal-end { border-radius: 8px; }

.cal-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid #f0f0f0;
}
.cal-hint { font-size: 12px; color: #888; }
.cal-clear {
  background: none;
  border: none;
  color: #e53e3e;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}

/* 반응형 */
@media (max-width: 768px) {
  .hsb-bar      { flex-direction: column; border-radius: 12px; }
  .hsb-divider  { width: 100%; height: 1px; margin: 0; }
  .hsb-btn      { border-radius: 0 0 12px 12px; padding: 14px; text-align: center; }
  .hsb-cal-panel { min-width: 280px; left: 0; right: 0; }
  .cal-months   { flex-direction: column; gap: 16px; }
  .cal-months-label { gap: 20px; }
}
</style>
